import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, ListItem, ListItemAvatar, ListItemText, Typography, Box, CircularProgress, Avatar } from '@mui/material';
import { useMode } from '../context/ModeContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';

const Conversations = () => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { mode } = useMode();
    const { currentUser: user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchConversations = async () => {
            try {
                const response = await api.get('/messaging/conversations');
                setConversations(response.data);
            } catch (err) {
                setError('Failed to load conversations');
            } finally {
                setLoading(false);
            }
        };
        fetchConversations();
    }, []);

    const handleConversationClick = (conversationId) => {
        navigate(`/transactions/${conversationId}`);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography variant="h6" color="error" align="center" mt={4}>
                {error}
            </Typography>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', p: 2 }}>
            <Typography variant="h4" gutterBottom>
                {mode === 'Borrowing' ? 'Your Conversations' : 'Lending Conversations'}
            </Typography>
            <List>
                {conversations
                    .filter(conv => mode === 'Borrowing' ?
                        conv.borrower_id === user.id :
                        conv.lender_id === user.id)
                    .map((conversation) => {
                        const isSender = conversation.last_message_sender_id === user.id;
                        const counterpartName = mode === 'Borrowing' ?
                            `${conversation.lender_first_name} ${conversation.lender_last_name}` :
                            `${conversation.borrower_first_name} ${conversation.borrower_last_name}`;

                        return (
                            <ListItem
                                button
                                key={conversation.id}
                                onClick={() => handleConversationClick(conversation.transaction_id)}
                                sx={{
                                    mb: 1,
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    '&:hover': {
                                        boxShadow: 3,
                                    },
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: isSender ? 'primary.light' : 'secondary.light' }}>
                                        {isSender ? (
                                            <CheckCircleOutlineIcon fontSize="small" />
                                        ) : (
                                            <MoveToInboxIcon fontSize="small" />
                                        )}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={counterpartName}
                                    secondary={
                                        <>
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color={isSender ? 'primary.main' : 'text.primary'}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                {isSender && 'You: '}
                                                {conversation.last_message_content}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0.5 }}
                                            >
                                                {new Date(conversation.last_message_at).toLocaleString([], {
                                                    dateStyle: 'short',
                                                    timeStyle: 'short'
                                                })}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                {conversations.length === 0 && (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                        No conversations found
                    </Typography>
                )}

            </List>
        </Box>
    );
};

export default Conversations;
